import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent, MouseEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FeatureFlagsClientFilter } from '../../../types/features/featureFlags';
import {
  buildFeatureFlagsSearchParams,
  getFeatureFlagsClientFilterFromSearchParams,
  getFeatureFlagsSearchFromSearchParams,
} from '../utils/featureFlagsQueryParams';

export const useFeatureFlagsUrlFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clientFilter, setClientFilterState] = useState<FeatureFlagsClientFilter>(
    () => getFeatureFlagsClientFilterFromSearchParams(searchParams),
  );
  const [search, setSearch] = useState(() => getFeatureFlagsSearchFromSearchParams(searchParams));

  useEffect(() => {
    const nextClientFilter = getFeatureFlagsClientFilterFromSearchParams(searchParams);
    const nextSearch = getFeatureFlagsSearchFromSearchParams(searchParams);

    setClientFilterState((currentClientFilter) => (
      currentClientFilter === nextClientFilter ? currentClientFilter : nextClientFilter
    ));
    setSearch((currentSearch) => (currentSearch === nextSearch ? currentSearch : nextSearch));
  }, [searchParams]);

  const setClientFilter = useCallback((nextClientFilter: FeatureFlagsClientFilter) => {
    setClientFilterState(nextClientFilter);
    setSearchParams(buildFeatureFlagsSearchParams(searchParams, nextClientFilter, search), { replace: true });
  }, [search, searchParams, setSearchParams]);

  const handleClientFilterChange = useCallback((
    _event: MouseEvent<HTMLElement>,
    nextFilter: FeatureFlagsClientFilter | null,
  ) => {
    if (nextFilter) {
      setClientFilter(nextFilter);
    }
  }, [setClientFilter]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextSearch = event.target.value;

    setSearch(nextSearch);
    setSearchParams(buildFeatureFlagsSearchParams(searchParams, clientFilter, nextSearch), { replace: true });
  }, [clientFilter, searchParams, setSearchParams]);

  return {
    clientFilter,
    handleClientFilterChange,
    handleSearchChange,
    search,
    setClientFilter,
  };
};
